import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Alert,
  Box,
  CircularProgress
} from '@mui/material';
import { clientInventoryAPI } from '../../services/api';

const UsageDialog = ({ open, onClose, item, mode = 'create', onSuccess }) => {
  const [formData, setFormData] = useState({
    dateUsed: '',
    horseName: '',
    injectionSite: '',
    notes: ''
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Initialize form data when item changes
  useEffect(() => {
    if (item && mode === 'edit') {
      setFormData({
        dateUsed: item.dateUsed ? new Date(item.dateUsed).toISOString().split('T')[0] : '',
        horseName: item.horseName || '',
        injectionSite: item.injectionSite || '',
        notes: item.notes || ''
      });
    } else {
      setFormData({
        dateUsed: new Date().toISOString().split('T')[0], // Default to today
        horseName: '',
        injectionSite: '',
        notes: ''
      });
    }
    setErrors({});
    setSubmitError('');
  }, [item, mode, open]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.dateUsed) {
      newErrors.dateUsed = 'Date used is required';
    } else if (new Date(formData.dateUsed) > new Date()) {
      newErrors.dateUsed = 'Date cannot be in the future';
    }

    if (!formData.horseName?.trim()) {
      newErrors.horseName = 'Horse name is required';
    } else if (formData.horseName.length > 255) {
      newErrors.horseName = 'Horse name must be less than 255 characters';
    }

    if (!formData.injectionSite?.trim()) {
      newErrors.injectionSite = 'Injection site is required';
    } else if (formData.injectionSite.length > 255) {
      newErrors.injectionSite = 'Injection site must be less than 255 characters';
    }

    if (formData.notes && formData.notes.length > 5000) {
      newErrors.notes = 'Notes must be less than 5000 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setSubmitError('');

      const payload = {
        dateUsed: formData.dateUsed,
        horseName: formData.horseName.trim(),
        injectionSite: formData.injectionSite.trim(),
        notes: formData.notes?.trim() || undefined
      };

      let response;
      if (mode === 'edit') {
        response = await clientInventoryAPI.updateTracking(item.tissueId, payload);
      } else {
        response = await clientInventoryAPI.markUsed(item.tissueId, payload);
      }

      if (response.success) {
        onSuccess();
        onClose();
      } else {
        setSubmitError(response.error || 'Failed to save usage information');
      }
    } catch (err) {
      console.error('Error saving usage information:', err);
      setSubmitError(err.message || 'An unexpected error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: undefined
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {mode === 'edit' ? 'Edit Usage Information' : 'Mark Tissue ID as Used'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Tissue ID: <strong>{item?.tissueId}</strong>
          </Typography>

          <TextField
            fullWidth
            label="Date Used *"
            type="date"
            value={formData.dateUsed}
            onChange={handleChange('dateUsed')}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: new Date().toISOString().split('T')[0] }}
            error={!!errors.dateUsed}
            helperText={errors.dateUsed || 'Date the vial was used'}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Horse Name *"
            value={formData.horseName}
            onChange={handleChange('horseName')}
            error={!!errors.horseName}
            helperText={errors.horseName || 'Name of the horse treated'}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Injection Site *"
            value={formData.injectionSite}
            onChange={handleChange('injectionSite')}
            error={!!errors.injectionSite}
            helperText={errors.injectionSite || 'Location where the treatment was administered'}
            sx={{ mb: 2 }}
            placeholder="e.g., Left Front Fetlock, Right Hock, etc."
          />

          <TextField
            fullWidth
            label="Notes (Optional)"
            multiline
            rows={4}
            value={formData.notes}
            onChange={handleChange('notes')}
            error={!!errors.notes}
            helperText={errors.notes || 'Add any additional information about this usage'}
            placeholder="e.g., Horse responded well to treatment, used for joint inflammation, etc."
          />

          {submitError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {submitError}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting}
          startIcon={submitting && <CircularProgress size={20} />}
        >
          {submitting ? 'Saving...' : mode === 'edit' ? 'Update' : 'Save Usage'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UsageDialog;

